/**
 * `/process` — the workflow editor, in forms.
 *
 * A drag-and-drop canvas was explicitly deferred (ADR-015). What an
 * administrator needs is not a picture of the spine but the ability to change
 * one field of it and see, before publishing, exactly what that does — which is
 * what the version lineage, the structural diff and the validation panel are.
 *
 * Two things on this screen are load-bearing rather than decorative.
 *
 * **The case count beside each version.** A version with live cases on it is
 * the procedure those proceedings are judged against. Forking it is not a
 * refactor, and the number is there so it does not feel like one.
 *
 * **D12 and D15 carry a lock, not a disabled dropdown.** Making either
 * system-generated is ADR-021's boundary — a question for AWEIL's legal cell —
 * and the screen says so rather than offering a control that 409s.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import type { CaseDetail, ProcessDetail } from '../api/client'
import { PublishBox, ValidationReport } from '../components/editor'
import { CitationChip, ErrorEnvelope } from '../components/bits'
import { readDay } from '../lib/blocks'
import { readDate } from '../lib/period'

export function Process() {
  const client = useQueryClient()
  const [tab, setTab] = useState<'documents' | 'clocks' | 'guards' | 'stages'>('documents')

  const versions = useQuery({
    queryKey: ['process-versions'],
    queryFn: () => api.listProcessVersions(),
  })
  const process = useQuery({ queryKey: ['process'], queryFn: () => api.getProcess() })

  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['process'] })
    void client.invalidateQueries({ queryKey: ['process-versions'] })
    void client.invalidateQueries({ queryKey: ['process-diff'] })
  }

  const openDraft = useMutation({
    mutationFn: () => api.openProcessDraft(),
    onSuccess: invalidate,
  })
  const publish = useMutation({
    mutationFn: (change_note: string) => api.publishProcess({ preflight: false, change_note }),
    onSuccess: invalidate,
  })
  const discard = useMutation({
    mutationFn: () => api.discardProcessDraft(),
    onSuccess: invalidate,
  })

  const detail = process.data
  const isDraft = detail?.status === 'DRAFT'
  const published = (versions.data ?? []).filter((v) => v.status === 'PUBLISHED')
  const latestPublished = published.length ? Math.max(...published.map((v) => v.version)) : null

  return (
    <div className="page">
      <header>
        <h1>Process</h1>
        <p className="small muted">
          The four-stage, twenty-document spine — stages, documents, clocks, decision points and
          guards. All of it is data, all of it is versioned, and every case is pinned to the
          version it runs on.
        </p>
      </header>

      <section className="card">
        <header>
          <h2>Versions</h2>
        </header>
        <div className="body">
          <table className="grid">
            <thead>
              <tr>
                <th>Version</th>
                <th>Status</th>
                <th>Cases pinned</th>
                <th>Published</th>
                <th>Change note</th>
                <th>Problems</th>
              </tr>
            </thead>
            <tbody>
              {(versions.data ?? []).map((row) => (
                <tr key={row.version}>
                  <td className="mono">v{row.version}</td>
                  <td>
                    <span className={`chip ${row.status === 'DRAFT' ? 'warn' : 'good'}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="mono">{row.cases_pinned}</td>
                  <td className="small">
                    {row.published_at ? (
                      <>
                        {readDay(row.published_at)} · {row.published_by}
                      </>
                    ) : (
                      <span className="faint">
                        forked from v{row.based_on_version} by {row.forked_by}
                      </span>
                    )}
                  </td>
                  <td className="small muted">{row.change_note ?? '—'}</td>
                  <td className="mono">{row.problems || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!isDraft ? (
            <>
              <p className="small muted">
                Publishing a version does not move a single existing case onto it. A proceeding
                is judged against the procedure that was in force when it happened; moving one
                across is a separate, reasoned act on the case itself.
              </p>
              <button
                type="button"
                onClick={() => openDraft.mutate()}
                disabled={openDraft.isPending}
              >
                {openDraft.isPending ? 'Opening…' : 'Edit — opens a draft'}
              </button>
            </>
          ) : null}
          <ErrorEnvelope error={openDraft.error} />
        </div>
      </section>

      {/* **Always here, not inside the draft's diff.** It stood under *What this
          draft changes*, which is drawn only while a draft version is open — so
          with nothing but published versions on the screen there was no way to
          move a case at all, short of opening a draft nobody wanted. A corrected
          version is published precisely so that cases can be moved to it
          (version 4, 5 Oct 2026: a case with no inquiry could not leave stage 1
          until it was moved). */}
      <MigrateACase />

      {isDraft && detail && latestPublished !== null ? (
        <>
          <Diff from={latestPublished} to={detail.version} />
          <section className="card">
            <header>
              <h2>Publish</h2>
            </header>
            <div className="body">
              <ValidationReport
                problems={detail.problems ?? []}
                emptyText="All validation rules pass. This draft is ready to publish."
              />
              <PublishBox
                label="Publish this version"
                busy={publish.isPending}
                disabled={(detail.problems ?? []).length > 0}
                disabledReason={
                  (detail.problems ?? []).length
                    ? `${(detail.problems ?? []).length} validation problem${(detail.problems ?? []).length === 1 ? '' : 's'} must be fixed first. The button stays on screen so the rule is visible.`
                    : undefined
                }
                onPublish={(note) => publish.mutate(note)}
              />
              <ErrorEnvelope error={publish.error} />
              <button type="button" className="quiet" onClick={() => discard.mutate()}>
                Discard this draft
              </button>
            </div>
          </section>
        </>
      ) : null}

      {detail ? (
        <>
          <nav className="tabs">
            {(['documents', 'clocks', 'guards', 'stages'] as const).map((name) => (
              <button
                key={name}
                type="button"
                className={tab === name ? 'active' : ''}
                onClick={() => setTab(name)}
              >
                {name.charAt(0).toUpperCase() + name.slice(1)}
              </button>
            ))}
          </nav>
          {tab === 'documents' ? (
            <Documents detail={detail} editable={isDraft} onChanged={invalidate} />
          ) : null}
          {tab === 'clocks' ? (
            <Clocks detail={detail} editable={isDraft} onChanged={invalidate} />
          ) : null}
          {tab === 'guards' ? <Guards detail={detail} /> : null}
          {tab === 'stages' ? (
            <Stages detail={detail} editable={isDraft} onChanged={invalidate} />
          ) : null}
        </>
      ) : null}
    </div>
  )
}

function Diff({ from, to }: { from: number; to: number }) {
  const diff = useQuery({
    queryKey: ['process-diff', from, to],
    queryFn: () => api.getProcessDiff(from, to),
  })
  return (
    <section className="card">
      <header>
        <h2>
          What this draft changes{' '}
          <span className="small faint mono">
            v{from} → v{to}
          </span>
        </h2>
      </header>
      <div className="body">
        {!(diff.data?.changes ?? []).length ? (
          <p className="small muted">Nothing yet.</p>
        ) : (
          <ul className="changes">
            {(diff.data?.changes ?? []).map((change, index) => (
              <li key={index}>
                <span className="chip neutral">{change.kind}</span>{' '}
                <span className="mono">{change.key}</span> — {change.detail}
                {change.before !== null && change.before !== undefined ? (
                  <div className="small faint">
                    <s>{String(change.before)}</s> → {String(change.after)}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

function Documents({
  detail,
  editable,
  onChanged,
}: {
  detail: ProcessDetail
  editable: boolean
  onChanged: () => void
}) {
  const edit = useMutation({
    mutationFn: (body: Parameters<typeof api.editProcessDocument>[0]) =>
      api.editProcessDocument(body),
    onSuccess: onChanged,
  })

  return (
    <section className="card">
      <header>
        <h2>Documents</h2>
      </header>
      <div className="body">
        <ErrorEnvelope error={edit.error} />
        <table className="grid documents">
          <thead>
            <tr>
              <th>#</th>
              <th>Title</th>
              <th>Short name</th>
              <th>Hindi</th>
              <th>Author</th>
              <th>Written by</th>
              <th>Cardinality</th>
              <th>Rule</th>
            </tr>
          </thead>
          <tbody>
            {(detail.documents ?? []).map((doc) => (
              <tr key={doc.n} data-document={doc.id}>
                <td className="mono">{doc.n}</td>
                <td>{doc.title}</td>
                <td>
                  {editable ? (
                    /* The rail-length display name. The register title above is
                       what the record reads; this is what the officer scans, and
                       V-DOC-SHORT-TITLE-SHORT refuses one that is not short. */
                    <input
                      aria-label={`Short name for ${doc.id}`}
                      defaultValue={doc.short_title ?? ''}
                      placeholder="falls back to the title"
                      onBlur={(event) =>
                        event.target.value.trim() !== (doc.short_title ?? '') &&
                        edit.mutate({
                          preflight: false,
                          n: doc.n,
                          short_title: event.target.value.trim() || undefined,
                        })
                      }
                    />
                  ) : (
                    (doc.short_title ?? <span className="muted">—</span>)
                  )}
                </td>
                <td className="deva">{doc.title_hi ?? '—'}</td>
                <td className="small">{doc.author}</td>
                <td>
                  {doc.upload_only ? (
                    /* A lock, not a dropdown that 409s. Making D12 or D15
                       system-generated is ADR-021's boundary and a question for
                       AWEIL's legal cell — raised, never clicked. */
                    <span
                      className="chip accent"
                      data-testid={`upload-only-${doc.id}`}
                      title="ADR-021 — written by a quasi-judicial officer who must apply their own mind"
                    >
                      upload only · ADR-021
                    </span>
                  ) : editable ? (
                    <select
                      value={doc.authoring}
                      onChange={(event) =>
                        edit.mutate({
                          preflight: false,
                          n: doc.n,
                          authoring: event.target.value as 'internal' | 'external',
                        })
                      }
                    >
                      <option value="internal">this section drafts it</option>
                      <option value="external">awaited from someone else</option>
                    </select>
                  ) : (
                    doc.authoring
                  )}
                </td>
                <td className="small">{doc.cardinality}</td>
                <td>
                  <CitationChip citation={doc.citation} ruleRefs={doc.rule_refs} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="small faint">
          D12 and D15 are written by quasi-judicial officers who must apply their own mind.
          Software from this section that drafted their findings or reasons would be a credible
          ground of challenge, so both are upload-only and the editor refuses to change that.
          Moving that boundary is a question for AWEIL&rsquo;s legal cell — raise it, do not
          click it.
        </p>
      </div>
    </section>
  )
}

function Clocks({
  detail,
  editable,
  onChanged,
}: {
  detail: ProcessDetail
  editable: boolean
  onChanged: () => void
}) {
  const edit = useMutation({
    mutationFn: (body: Parameters<typeof api.editClock>[0]) => api.editClock(body),
    onSuccess: onChanged,
  })

  return (
    <section className="card">
      <header>
        <h2>Periods</h2>
      </header>
      <div className="body">
        <ErrorEnvelope error={edit.error} />
        {(detail.clocks ?? []).map((clock) => (
          <div className="clock" key={clock.key}>
            <div className="head">
              <strong>{clock.label}</strong>
              <span
                className={`chip ${clock.authority === 'statutory' ? 'accent' : 'neutral'}`}
              >
                {clock.authority === 'statutory' ? 'Statutory' : 'Office-set'}
              </span>
              <CitationChip citation={clock.citation} />
            </div>
            <div className="source">{clock.source}</div>
            <div className="small muted">
              {clock.days_allowed !== null
                ? `${clock.days_allowed} days`
                : `${clock.months_allowed} months`}{' '}
              from <span className="mono">{clock.starts_on_event}</span> ·{' '}
              {clock.starts_on_act || 'no act named'}
            </div>
            {editable ? (
              <div className="row form">
                <label>
                  Extension cap
                  <input
                    defaultValue={clock.max_extension_days ?? ''}
                    onBlur={(event) =>
                      edit.mutate({
                        preflight: false,
                        key: clock.key,
                        max_extension_days: event.target.value
                          ? Number(event.target.value)
                          : null,
                      })
                    }
                  />
                </label>
                <label>
                  Authority
                  <select
                    defaultValue={clock.authority}
                    onChange={(event) =>
                      edit.mutate({
                        preflight: false,
                        key: clock.key,
                        authority: event.target.value as 'statutory' | 'office_set',
                      })
                    }
                  >
                    <option value="statutory">Statutory</option>
                    <option value="office_set">Office-set</option>
                  </select>
                </label>
              </div>
            ) : null}
          </div>
        ))}
        <p className="small faint">
          Whether a period is statutory or office-set is shown wherever it appears. An officer
          who believes a self-imposed deadline is law will refuse an extension he is entitled to
          grant.
        </p>
      </div>
    </section>
  )
}

function Guards({ detail }: { detail: ProcessDetail }) {
  return (
    <section className="card">
      <header>
        <h2>Stage-entry gates</h2>
      </header>
      <div className="body">
        {(detail.guards ?? []).map((guard) => (
          <div className="guard" key={`${guard.stage_id}-${guard.id}`}>
            <div className="head">
              <span className="mono">{guard.id}</span>
              <span className={`chip ${guard.level === 'block' ? 'bad' : 'warn'}`}>
                {guard.level}
              </span>
              <span className="small faint mono">entry to {guard.stage_id}</span>
              <CitationChip citation={guard.citation} />
            </div>
            {/* The English comes off the server. `explain()` is the one place a
                condition becomes words. */}
            <div className="english small">{guard.english}</div>
            <div className="small muted">{guard.message}</div>
          </div>
        ))}
      </div>
    </section>
  )
}

function Stages({
  detail,
  editable,
  onChanged,
}: {
  detail: ProcessDetail
  editable: boolean
  onChanged: () => void
}) {
  const edit = useMutation({
    mutationFn: (body: Parameters<typeof api.editStage>[0]) => api.editStage(body),
    onSuccess: onChanged,
  })
  return (
    <section className="card">
      <header>
        <h2>Stages</h2>
      </header>
      <div className="body">
        <ErrorEnvelope error={edit.error} />
        {(detail.stages ?? []).map((stage) => (
          <div className="row form" key={stage.id}>
            <span className="mono">{stage.id}</span>
            <label>
              Title
              <input
                defaultValue={stage.title}
                disabled={!editable}
                onBlur={(event) =>
                  event.target.value !== stage.title &&
                  edit.mutate({
                    preflight: false,
                    stage_id: stage.id,
                    title: event.target.value,
                  })
                }
              />
            </label>
            <label>
              Hindi
              <input
                className="deva"
                defaultValue={stage.title_hi ?? ''}
                disabled={!editable}
                onBlur={(event) =>
                  event.target.value !== (stage.title_hi ?? '') &&
                  edit.mutate({
                    preflight: false,
                    stage_id: stage.id,
                    title_hi: event.target.value,
                  })
                }
              />
            </label>
            <label>
              Position
              <input
                defaultValue={stage.n}
                disabled={!editable}
                onBlur={(event) =>
                  Number(event.target.value) !== stage.n &&
                  edit.mutate({
                    preflight: false,
                    stage_id: stage.id,
                    n: Number(event.target.value),
                  })
                }
              />
            </label>
          </div>
        ))}
      </div>
    </section>
  )
}

/**
 * Moving a case to a corrected process version, from the screen where process
 * versions live.
 *
 * It sat at the foot of every case page, where an officer met it constantly and
 * used it approximately never — Archit, 5 Sep: *"move it to the process screen,
 * I will see it later, I don't think it will be very useful."* It is an
 * administrator's act, not an officer's, and this is the administrator's
 * screen.
 *
 * **It is per case, and this screen is not**, so it opens with a case to work
 * on. `cases_pinned` on the versions table is a count and nothing lists which
 * cases are on a version, so the picker reads the register rather than the
 * version — no server change, and the administrator picks the file they were
 * asked about.
 *
 * Why it must remain reachable at all: v1 shipped a rule that reads a clock by
 * a key it never defines, so the comparison is undecidable, undecidable is
 * false, and the no-reply branch could never enter the formal inquiry — the
 * branch Rule 14(4) exists to make lawful. Every case opened under v1 carries
 * that defect until somebody moves it.
 */
export function MigrateACase() {
  const [caseId, setCaseId] = useState('')
  const cases = useQuery({ queryKey: ['cases'], queryFn: () => api.listCases() })
  const detail = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => api.getCase(caseId),
    enabled: Boolean(caseId),
  })
  return (
    <section className="panel" data-testid="migrate-a-case">
      <header>
        <h2>Move a case to a corrected version</h2>
      </header>
      <div className="body">
        <p className="small muted">
          A case is pinned to the version it was opened under and stays there — publishing a new
          version moves nothing. A case is moved only here, only with a reason, and always with
          an audit entry.
        </p>
        <label className="field">
          <span className="small muted">The case</span>
          <select
            aria-label="The case to move"
            value={caseId}
            onChange={(event) => setCaseId(event.target.value)}
            data-testid="migrate-case-picker"
          >
            <option value="">Pick a case…</option>
            {(cases.data ?? []).map((row) => (
              <option key={row.id} value={row.id}>
                {row.file_number ?? 'Not yet numbered'}
              </option>
            ))}
          </select>
        </label>
        {/* Keyed by the case: the form holds what was chosen, typed and done
            for one case, and none of it — least of all "Moved to v6" — may be
            left standing on the next case picked. */}
        {detail.data ? (
          <Migration
            key={detail.data.id}
            data={detail.data}
            onMigrated={() => void detail.refetch()}
          />
        ) : null}
      </div>
    </section>
  )
}

/**
 * **A version that is not the latest may be one the latest was published to
 * correct.** Both stay on the list for good — published versions are frozen —
 * and the earlier one's own note still reads like the fix it was meant to be.
 * The note that says otherwise is the latest version's, so it is printed here,
 * where the choice is being made, and not left for the officer to go and find.
 * Nothing is shown for the latest itself.
 */
export function NotTheLatest({
  target,
  pinned,
  newest,
  note,
}: {
  target: number
  pinned: number
  newest: number
  note: string | null
}) {
  if (target === newest) return null
  return (
    <div className="small" style={{ color: 'var(--warn)' }} data-testid="migration-not-latest">
      {target > pinned ? (
        <p>
          v{target} is <strong>not the latest</strong> published version — v{newest} is. A
          version published after this one may have been published to correct it.
        </p>
      ) : null}
      <p>
        What v{newest}'s own note says: “{note ?? 'no note'}”
      </p>
    </div>
  )
}

/**
 * **What the move does to the case's stage — said before it is made.** A
 * version that changes nothing this case has acted on can still carry it into
 * another stage the moment it is moved there (a case held back by a gate the
 * new version corrects is moved for exactly that), and a preview that read
 * "0 of 1 changes reach this case" over a move that then changed its stage
 * told the officer half. Both sentences are the server's, from the same
 * reading the move itself uses; nothing is composed here.
 */
export function StageOfTheMove({
  note,
  reason,
}: {
  note?: string | null
  reason?: string | null
}) {
  if (!note) return null
  return (
    <p className="small" data-testid="migration-stage">
      <strong>The case's stage.</strong> {note}
      {reason ? <span className="muted"> {reason}</span> : null}
    </p>
  )
}

/**
 * What the move did, in the server's own words, kept on screen after it. The
 * preview said what it *would* do; the receipt says what it did.
 */
export function MovedLine({
  to,
  note,
  why,
}: {
  to: number
  note: string | null
  why: string | null
}) {
  return (
    <p className="small" role="status" data-testid="migration-done">
      <strong>Moved to v{to}.</strong> {note ?? ''}
      {why ? <span className="muted"> {why}</span> : null}
    </p>
  )
}

/**
 * Moving a case to a corrected process version. **Never silently.**
 *
 * The preview is the point. An audit row nobody reads is not the same as "never
 * silent": before an officer moves a case they see which of the changes reach a
 * document this case has already acted on, and which are about parts of the
 * procedure it has not reached. The reason is required, at least twenty
 * characters, and it goes onto the case event and the audit trail.
 */
export function Migration({ data, onMigrated }: { data: CaseDetail; onMigrated: () => void }) {
  const [target, setTarget] = useState<number | null>(null)
  const [reason, setReason] = useState('')
  // What the move did, in the server's own words, kept on screen after it.
  // The preview said what it *would* do; the receipt says what it did, and an
  // officer who has just moved a live case is owed the second as plainly as
  // the first.
  const [moved, setMoved] = useState<{
    to: number
    note: string | null
    why: string | null
  } | null>(null)

  const versions = useQuery({
    queryKey: ['process-versions'],
    queryFn: () => api.listProcessVersions(),
  })
  const preview = useQuery({
    queryKey: ['migration-preview', data.id, target],
    queryFn: () => api.previewCaseMigration(data.id, target as number),
    enabled: target !== null,
  })
  const migrate = useMutation({
    mutationFn: () =>
      api.migrateProcessVersion(data.id, {
        preflight: false,
        to_version: target as number,
        reason,
      }),
    onSuccess: (receipt) => {
      const said = receipt.detail ?? {}
      const note = said['stage_note']
      const why = said['stage_reason']
      setMoved({
        to: target as number,
        note: typeof note === 'string' ? note : null,
        why: typeof why === 'string' ? why : null,
      })
      setReason('')
      setTarget(null)
      onMigrated()
    },
  })

  const published = (versions.data ?? []).filter((v) => v.status === 'PUBLISHED')
  const others = published.filter((v) => v.version !== data.process_version)
  // A later version is not always the one to move to: a version can be
  // published and then corrected the same day, and both stay on the list for
  // good. The newest is named, so the one in between is not picked by its
  // being "later".
  const newest = Math.max(...published.map((v) => v.version))
  const latest = published.find((v) => v.version === newest)
  const history = (data.events ?? []).filter(
    (event) => (event as Record<string, unknown>)['kind'] === 'case.process_version.migrated',
  )
  const short = reason.trim().length < 20

  return (
    <div className="card">
      <header>
        <h2>Process version</h2>
        <span className="small muted">
          pinned to <span className="mono">v{data.process_version}</span>
        </span>
      </header>
      <div className="body">
        <p className="small muted">
          This case runs on the procedure that was in force when it opened. Publishing a newer
          version does not move it; moving it is a separate act, with a reason on the record.
        </p>

        {moved ? <MovedLine to={moved.to} note={moved.note} why={moved.why} /> : null}

        {others.length === 0 ? (
          <p className="small faint">No other published version exists.</p>
        ) : (
          <div className="row form">
            <label>
              Move to
              <select
                value={target ?? ''}
                onChange={(event) => {
                  setMoved(null)
                  setTarget(event.target.value ? Number(event.target.value) : null)
                }}
              >
                <option value="">choose a version…</option>
                {/* The direction is the one thing an officer can judge
                    instantly and the machine can state for free. On a case
                    pinned to the newest version, everything this list offers
                    is a move backwards — into whatever the later version was
                    published to fix — and it used to be phrased exactly like
                    an upgrade (walkthrough log entry 21). */}
                {others.map((version) => (
                  <option key={version.version} value={version.version}>
                    v{version.version} ·{' '}
                    {version.version === newest
                      ? 'the latest'
                      : version.version > data.process_version
                        ? `later than this case, but NOT the latest (v${newest} is)`
                        : 'EARLIER than this case'}{' '}
                    — {version.change_note ?? 'no note'}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {preview.data ? (
          <div data-testid="migration-preview">
            {target !== null && target < data.process_version ? (
              <p className="small" style={{ color: 'var(--warn)' }}>
                This is a move <strong>backwards</strong>, to a version published before the one
                this case runs on — including whatever v{data.process_version} was published to
                fix. The reason you record should say why the earlier procedure is the right
                one.
              </p>
            ) : null}
            {target !== null ? (
              <NotTheLatest
                target={target}
                pinned={data.process_version}
                newest={newest}
                note={latest?.change_note ?? null}
              />
            ) : null}
            <p className="small">
              <strong>{preview.data.touching_this_case}</strong> of{' '}
              {(preview.data.effects ?? []).length} change
              {(preview.data.effects ?? []).length === 1 ? '' : 's'} reach something this case
              has already acted on.
            </p>
            <StageOfTheMove note={preview.data.stage_note} reason={preview.data.stage_reason} />
            <ul className="changes">
              {(preview.data.effects ?? []).map((effect, index) => (
                <li key={index} className={effect.affects_this_case ? 'touches' : ''}>
                  <span className="chip neutral">{effect.change.kind}</span>{' '}
                  <span className="mono">{effect.change.key}</span> — {effect.change.detail}
                  {effect.why ? <div className="small warn-text">{effect.why}</div> : null}
                </li>
              ))}
            </ul>
            <label>
              Why this case is being moved
              <textarea
                rows={2}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
            <div className="row">
              <button
                type="button"
                disabled={short || migrate.isPending}
                onClick={() => migrate.mutate()}
              >
                {migrate.isPending ? 'Moving…' : `Move this case to v${target}`}
              </button>
              <span className="small muted">
                {short
                  ? `${20 - reason.trim().length} more characters needed — this goes on the record`
                  : 'Written to the case event and the audit trail.'}
              </span>
            </div>
            <ErrorEnvelope error={migrate.error} />
          </div>
        ) : null}

        {history.length ? (
          <>
            <h3 className="small" style={{ marginTop: 16 }}>
              Migration history
            </h3>
            <ul className="ledger" data-testid="migration-history">
              {history.map((event, index) => {
                const row = event as Record<string, unknown>
                const payload = (row['payload'] ?? {}) as Record<string, unknown>
                return (
                  <li key={index}>
                    v{String(payload['from_version'])} → v{String(payload['to_version'])} ·{' '}
                    {readDate(String(row['occurred_on']))} · {String(row['actor'] ?? 'unknown')}
                    <div className="small muted">{String(payload['reason'] ?? '')}</div>
                  </li>
                )
              })}
            </ul>
          </>
        ) : null}
      </div>
    </div>
  )
}
